import type { Command, GameState, PassCardsData, PlayCardData } from './definitions';
import { GameAction, RoundStage } from './definitions';
import type { ActionData } from './general_definitions';
import { Rank, Suit } from './general_definitions';
import { assumeType, getCardString, getRank, getSuit } from './general_utils';
import { getPlayerHandName, getPlayerPassHandName } from './utils';

const validNextStages = [
	RoundStage.PassEnd,
	RoundStage.TrickEnd,
	RoundStage.RoundEnd,
	RoundStage.GameOver,
];

function can(action: GameAction | Command, gameState: GameState, position?: number, data?: ActionData): boolean {
	if (!action) {
		return false;
	}

	if (typeof action !== 'string' && action.command) {
		assumeType<Command>(action);
		data = action.data;
		position = action.position;
		action = action.command as GameAction;
	}

	switch (action) {
		case GameAction.PassCards:
			if (gameState.roundStage !== RoundStage.Pass || !data || typeof position === 'undefined') {
				return false;
			}

			return canPassCards(data, gameState, position);
		case GameAction.PlayCard:
			if (gameState.roundStage !== RoundStage.Trick || !data || typeof position === 'undefined') {
				return false;
			}

			return canPlayCard(data, gameState, position);
		case GameAction.Togglecard:
			if (gameState.roundStage !== RoundStage.Pass || !data || typeof position === 'undefined') {
				return false;
			}

			return canToggleCard(data, gameState, position);
		case GameAction.Deal:
			return gameState.roundStage === RoundStage.Deal;
		case GameAction.NextStage:
			return validNextStages.includes(gameState.roundStage);
		case GameAction.NewGame:
			return true; // Always allow new game
		case GameAction.LoadGame:
			return true; // Always allow load game
		default:
			return false;
	}
}

function canPassCards(data: ActionData, gameState: GameState, position: number): boolean {
	assumeType<PassCardsData>(data);

	//First check if the from player actually has the cards
	const playerHandName = getPlayerHandName(position);

	for (const c of data?.cards ?? []) {
		const card = gameState.deck.find((cd) => getCardString(cd) === c);

		if (card?.pile !== playerHandName) {
			return false;
		}
	}

	const passHandName = getPlayerPassHandName(position);
	//check if the player is already passing cards
	const passCards = gameState.deck.filter((c) => c.pile === passHandName);
	return passCards.length === 0;
}

function canPlayCard(data: ActionData, gameState: GameState, position: number): boolean {
	assumeType<PlayCardData>(data);
	const { card } = data;

	//This player's turn?
	if (gameState.currentPlayer !== position) {
		return false;
	}

	const currentRound = gameState?.rounds?.at(-1);
	const currentTrick = currentRound?.tricks?.at(-1);

	//player hasn't played?
	if (typeof currentTrick?.cardsPlayed[position] !== 'undefined' && currentTrick?.cardsPlayed[position] !== null) {
		return false;
	}

	const suit = getSuit(card);

	//Lead of the first trick must be the 2 of clubs
	if (currentTrick?.leadPlayer === position && (currentRound?.tricks ?? []).length === 1 && (currentTrick?.cardsPlayed ?? []).length === 0 && (suit !== Suit.Clubs || getRank(card) !== Rank.Two)) {
		return false;
	}

	//Can't play a hearts on the first round
	if ((currentRound?.tricks ?? []).length === 1 && suit === Suit.Hearts) {
		return false;
	}

	//Is it lead and a heart but hearts haven't been broken yet and the player has cards other than hearts?
	if (currentTrick?.leadPlayer === position && suit === Suit.Hearts && !gameState.heartsBroken && playerHasOtherSuits(gameState, position, Suit.Hearts)) {
		return false;
	}

	if (currentTrick && currentTrick.leadPlayer !== position && typeof currentTrick.cardsPlayed[currentTrick.leadPlayer] === 'undefined') {
		return false;
	}

	//Is it the same suit as the lead card?
	if (currentTrick && currentTrick.leadPlayer !== position && getSuit(currentTrick.cardsPlayed[currentTrick.leadPlayer]) !== suit && playerHasSuit(gameState, position, getSuit(currentTrick.cardsPlayed[currentTrick.leadPlayer]))) {
		return false;
	}

	return true;
}

function canToggleCard(data: ActionData, gameState: GameState, position: number): boolean {
	assumeType<PlayCardData>(data);
	const { card: cardString } = data;
	const passHand = getPlayerPassHandName(position);
	const card = gameState.deck.find((c) => getCardString(c) === cardString);
	return card?.pile === getPlayerHandName(position) && gameState.deck.filter((c) => c.pile === passHand).length < 3;
}

function playerHasOtherSuits(gameState: GameState, player: number, suit: Suit): boolean {
	const deck = gameState.deck;
	const handName = getPlayerHandName(player);

	for (const card of deck) {
		if (card.pile === handName && card.suit !== suit) {
			return true;
		}
	}

	return false;
}

function playerHasSuit(gameState: GameState, player: number, suit: Suit): boolean {
	const deck = gameState.deck;
	const handName = getPlayerHandName(player);

	for (const card of deck) {
		if (card.pile === handName && card.suit === suit) {
			return true;
		}
	}

	return false;
}

export const Rules = {
	can,
};
